/** Iter 632 — customer wizard · Photo step (moved verbatim from EngageWizardSteps.jsx). */
import React, { useRef } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import ExtraPhotosControls from "@/features/customerWizard/ExtraPhotosControls";

/**
 * Iter-558 — PhotoStep: right after connect: glowing success, 📷 camera /
 * 🖼 photos, skip-48h. Doubles as the waiting-for-till screen.
 */
export default function PhotoStep({
  photo,
  waitingMsg,
  onPhoto,
  onSkip,
  skipChosen,
  // Iter-627 — extra photos + one-tap collage (main wizard only).
  extraPhotos = null,
  onAddExtra,
  onMakeCollage,
  collageBusy = false,
}) {
  const camRef = useRef(null);
  const galRef = useRef(null);

  const readTo = (cb) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result || "").split(",")[1] || "";
      if (b64) cb({ b64, previewUrl });
    };
    reader.readAsDataURL(file);
  };
  const readFile = readTo(onPhoto);

  return (
    <div className="text-center" data-testid="engage-photo-step">
      <div
        className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-white text-sm font-black px-5 py-2 shadow-[0_0_24px_rgba(16,185,129,0.55)] animate-pulse"
        data-testid="engage-qr-success"
      >
        <CheckCircle2 className="w-4 h-4" /> QR scanned successfully!
      </div>
      <p className="mt-4 text-sm text-slate-600">
        Snap a photo of your visit to share — it unlocks your discount and points.
      </p>
      {photo?.previewUrl && (
        <div className="mt-3" data-testid="engage-photo-ready">
          <img src={photo.previewUrl} alt="Your visit" className="mx-auto max-h-40 rounded-xl border border-slate-200" />
          <p className="mt-1.5 text-xs font-bold text-emerald-700">Photo ready ✓ — we&apos;ll help you post it next.</p>
        </div>
      )}
      {/* Iter-627 — extras + collage while waiting / before sharing. */}
      {photo && onAddExtra && (
        <ExtraPhotosControls
          extraPhotos={extraPhotos}
          onAddExtra={onAddExtra}
          onMakeCollage={onMakeCollage}
          collageBusy={collageBusy}
        />
      )}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          data-testid="engage-open-camera"
          onClick={() => camRef.current?.click()}
          className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold py-3.5 transition-colors"
        >
          📷 Open camera
        </button>
        <button
          type="button"
          data-testid="engage-choose-photo"
          onClick={() => galRef.current?.click()}
          className="rounded-xl bg-white border border-slate-300 hover:border-slate-500 text-slate-800 text-sm font-bold py-3.5 transition-colors"
        >
          🖼 Choose from my photos instead
        </button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={readFile} data-testid="engage-camera-input" />
      <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={readFile} data-testid="engage-gallery-input" />
      {waitingMsg && (
        <div
          className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600"
          data-testid="engage-photo-waiting-note"
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 shrink-0" /> {waitingMsg}
        </div>
      )}
      {skipChosen ? (
        <p className="mt-4 text-xs font-bold text-slate-500" data-testid="engage-skip-noted">
          ⏭ Skipped — you&apos;ll pay full price now and can post within 48 hours to earn the points.
        </p>
      ) : onSkip ? (
        <button
          type="button"
          data-testid="engage-skip-48h"
          onClick={onSkip}
          className="mt-4 text-xs font-bold text-slate-500 underline hover:text-slate-800"
        >
          ⏭ Skip for 48 hours — pay full price now (+$0.20 service fee, refunded as 20 pts if you post in time)
        </button>
      ) : null}
    </div>
  );
}
